import React, { lazy, Suspense, useState, useEffect } from "react";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import Loader from "../components/reusable-loader/Loader";
import ProtectedRoute from "./ProtectedRoutes";
import Login from "../pages/auth/Login";
import { useOktaAuth, LoginCallback } from "@okta/okta-react";
import { oktaAuth } from "../utils/oktaConfig";
import { useDispatch } from "react-redux";
import { clearAuthToken, setAuthToken } from "../redux/AuthSlice";
import createApiCall from "../utils/Api";
// import MainPage from "../pages/main-pages/MainPage";
// import InspectorsList from "../pages/main-pages/manage-inspectors/Inspectors-list/InspectorsList";
// import ManageAdmins from "../pages/main-pages/manage-admins/admins-list/AdminsList";
// import SubmittedReports from "../pages/main-pages/submitted-reports/SubmittedReports";
// import ProtectedRoute from "./ProtectedRoutes";
// import InspectorReportDetails from "../pages/main-pages/manage-inspectors/details/Details";

let oktaMeRequest: Promise<any> | null = null;
let oktaMeRequestToken: string | null = null;

// Keep lazy component identities stable. Declaring these inside AppRoutes
// remounts the active page whenever auth or the URL changes.
const MainPage = lazy(() => import("../pages/main-pages/MainPage"));
const InspectorsList = lazy(
  () =>
    import("../pages/main-pages/manage-inspectors/Inspectors-list/InspectorsList"),
);
const ManageAdmins = lazy(
  () => import("../pages/main-pages/manage-admins/admins-list/AdminsList"),
);
const SubmittedReports = lazy(
  () => import("../pages/main-pages/submitted-reports/SubmittedReports"),
);
const InspectorReportDetails = lazy(
  () => import("../pages/main-pages/manage-inspectors/details/Details"),
);

const isOktaLoginInProgress = () =>
  sessionStorage.getItem("okta_login_pending") === "true" ||
  oktaAuth.isLoginRedirect();

const AppRoutes = () => {
  const { authState } = useOktaAuth();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const [isAuthenticated, setIsAuthenticated] = useState<string | null>(
    localStorage.getItem("tobacco_access_token"),
  );
  const [isAuthLoading, setIsAuthLoading] = useState(
    !localStorage.getItem("tobacco_access_token") && isOktaLoginInProgress(),
  );
  const hasCmsToken =
    Boolean(isAuthenticated && isAuthenticated.length) ||
    Boolean(localStorage.getItem("tobacco_access_token"));

  useEffect(() => {
    const completeOktaLogin = async () => {
      const existingCmsToken = localStorage.getItem("tobacco_access_token");

      if (existingCmsToken) {
        setIsAuthenticated(existingCmsToken);
        setIsAuthLoading(false);
        return;
      }

      if (!isOktaLoginInProgress()) {
        setIsAuthLoading(false);
        return;
      }

      setIsAuthLoading(true);

      if (authState === null) {
        return;
      }

      if (!authState.isAuthenticated) {
        return;
      }

      const oktaToken = authState.accessToken?.accessToken;
      if (!oktaToken) {
        return;
      }

      try {
        if (!oktaMeRequest || oktaMeRequestToken !== oktaToken) {
          oktaMeRequestToken = oktaToken;
          oktaMeRequest = createApiCall()
            .get("/admin/me", {
              headers: {
                Authorization: `Bearer ${oktaToken}`,
              },
            })
            .finally(() => {
              oktaMeRequest = null;
              oktaMeRequestToken = null;
            });
        }

        const response = await oktaMeRequest;

        const userDetails = response?.data?.data ?? response?.data;
        const cmsToken = userDetails?.accessToken || userDetails?.accesstoken;

        if (!cmsToken) {
          throw new Error("Okta user lookup did not return an access token");
        }

        // The CMS response has the same shape as the normal login response.
        const cmsUserDetails = { ...userDetails, accessToken: cmsToken };
        localStorage.setItem("tobacco_access_token", cmsToken);
        sessionStorage.setItem("okta_authenticated", "true");
        sessionStorage.removeItem("okta_login_pending");
        sessionStorage.removeItem("okta_logout_requested");
        localStorage.setItem("tobocco_user_name", JSON.stringify(cmsUserDetails));
        dispatch(setAuthToken(cmsUserDetails));
        setIsAuthenticated(cmsToken);
        oktaAuth.tokenManager.clear();

        if (
          location.pathname === "/login" ||
          location.pathname === "/login/callback"
        ) {
          navigate("/manage-inspector", { replace: true });
        }
      } catch (error) {
        console.error("Okta user lookup error:", error);
        sessionStorage.removeItem("okta_authenticated");
        sessionStorage.removeItem("okta_login_pending");
        localStorage.removeItem("tobacco_access_token");
        dispatch(clearAuthToken());
        oktaAuth.tokenManager.clear();
        setIsAuthenticated(null);
      } finally {
        setIsAuthLoading(false);
      }
    };

    void completeOktaLogin();
  }, [authState, dispatch, location.pathname, navigate]);

  return (
    <Suspense fallback={<Loader />}>
      <Routes>
        <Route
          path="/login"
          element={
            oktaAuth.isLoginRedirect() ? (
              <LoginCallback />
            ) : isAuthLoading ? (
              <Loader />
            ) : hasCmsToken ? (
              <Navigate to="/manage-inspector" replace />
            ) : (
              <Login setIsAuthenticated={setIsAuthenticated} />
            )
          }
        />
        <Route
          path="/login/callback"
          element={<LoginCallback />}
        />
        <Route
          element={
            <ProtectedRoute
              isAuthenticated={isAuthenticated}
              isAuthLoading={isAuthLoading}
            >
              <MainPage setIsAuthenticated={setIsAuthenticated} />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to="/manage-inspector" replace />} />
          <Route path="manage-inspector" element={<InspectorsList />} />
          <Route path="manage-admin" element={<ManageAdmins />} />
          <Route path="submitted-reports" element={<SubmittedReports />} />
          <Route
            path="inspector-report/:id"
            element={<InspectorReportDetails />}
          />
        </Route>
      </Routes>
    </Suspense>
  );
};

export default AppRoutes;
